import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSession } from '../services/api';
import {
  ArrowLeft,
  CheckCircle2,
  Clock3,
  Phone,
  User,
  CalendarClock,
  MessageSquareText,
  Hash,
  ListChecks,
  ToggleLeft,
  Type as TypeIcon,
  HelpCircle,
} from 'lucide-react';

/* -------- Type metadata (light tokens) -------- */
const TYPE_META = {
  nps: {
    label: 'Escala NPS',
    badgeClass: 'border-[#2E8B57]/30 text-[#1F5C3A] bg-[#EAF7EE]',
    kind: 'number',
    valueLabel: 'Nota',
    icon: Hash,
  },
  escala: {
    label: 'Escala',
    badgeClass: 'border-[#2E8B57]/30 text-[#1F5C3A] bg-[#EAF7EE]',
    kind: 'number',
    valueLabel: 'Nota',
    icon: Hash,
  },
  sim_nao: {
    label: 'Sim / Não',
    badgeClass: 'border-[#D7A64A]/40 text-[#7B4B0C] bg-[#FFF4E4]',
    kind: 'boolean',
    valueLabel: 'Resposta',
    icon: ToggleLeft,
  },
  texto: {
    label: 'Texto livre',
    badgeClass: 'border-slate-300 text-slate-600 bg-slate-50',
    kind: 'text',
    valueLabel: 'Comentário',
    icon: TypeIcon,
  },
  multipla_escolha: {
    label: 'Múltipla escolha',
    badgeClass: 'border-violet-300/50 text-violet-700 bg-violet-50',
    kind: 'text',
    valueLabel: 'Resposta',
    icon: ListChecks,
  },
  multipla_escolha_multi: {
    label: 'Múltipla seleção',
    badgeClass: 'border-indigo-300/50 text-indigo-700 bg-indigo-50',
    kind: 'list',
    valueLabel: 'Resposta',
    icon: ListChecks,
  },
};

const KIND_LABEL = {
  number: 'Numérica',
  boolean: 'Sim/Não',
  list: 'Lista',
  text: 'Texto',
};

/* -------- Helpers -------- */
const formatDate = (value) => (value ? new Date(value).toLocaleString('pt-BR') : null);

const getInitials = (name) => {
  if (!name) return null;
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/* -------- Sub-components -------- */
const InfoBlock = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-start gap-2.5 rounded-xl border border-slate-200/70 bg-white px-3.5 py-2.5 shadow-[0_1px_4px_rgba(15,23,42,0.04)] min-w-[150px]">
    <div
      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        tone || 'bg-slate-100 text-slate-500'
      }`}
    >
      <Icon size={15} />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-800 whitespace-nowrap mt-0.5">
        {value}
      </div>
    </div>
  </div>
);

/* -------- Main -------- */
const SessionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!id) return;
      try {
        const s = await getSession(id);
        setData(s);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="px-2 sm:px-6 pb-12 sm:pb-16">
        <div
          className="max-w-[1280px] mx-auto rounded-2xl sm:rounded-3xl border p-3 sm:p-5 md:p-6 overflow-hidden"
          style={{
            borderColor: 'rgba(15, 23, 42, 0.06)',
            background:
              'radial-gradient(900px circle at 10% -20%, rgba(251, 236, 214, 0.55) 0%, transparent 55%), radial-gradient(1000px circle at 90% 0%, rgba(214, 234, 248, 0.55) 0%, transparent 60%), #F6F4F1',
          }}
        >
          <div className="rounded-2xl border border-slate-200/70 bg-white p-6 text-slate-500 text-sm">
            Carregando…
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-2 sm:px-6 pb-12 sm:pb-16">
        <div
          className="max-w-[1280px] mx-auto rounded-2xl sm:rounded-3xl border p-3 sm:p-5 md:p-6 overflow-hidden"
          style={{
            borderColor: 'rgba(15, 23, 42, 0.06)',
            background:
              'radial-gradient(900px circle at 10% -20%, rgba(251, 236, 214, 0.55) 0%, transparent 55%), radial-gradient(1000px circle at 90% 0%, rgba(214, 234, 248, 0.55) 0%, transparent 60%), #F6F4F1',
          }}
        >
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center">
            <HelpCircle size={32} className="text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-700">Sessão não encontrada</p>
            <button
              onClick={() => navigate('/feedback-hero/respostas')}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
            >
              <ArrowLeft size={14} />
              Voltar para Respostas
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sessionName =
    data.cliente_nome || data.nome || data.cliente || `Sessão #${data.id}`;
  const naoIdentificado =
    !data.cliente_nome && !data.nome && !data.cliente;
  const isDone = !!data.finalizado_em;
  const respostas = Array.isArray(data.respostas) ? data.respostas : [];
  const initials = getInitials(sessionName);
  const totalRespostas = respostas.length;
  const respondidas = respostas.filter((r) => {
    const numero = r.resposta_numero ?? r.respostaNumero ?? (typeof r.resposta === 'number' ? r.resposta : null);
    const texto = r.resposta_texto ?? r.respostaTexto ?? (typeof r.resposta === 'string' ? r.resposta : '');
    const lista = r.resposta_lista ?? r.respostaLista ?? (Array.isArray(r.resposta) ? r.resposta : null);
    const bool = r.resposta_bool ?? r.respostaBool ?? r.respostaBoolean ?? null;
    return numero != null || (texto && String(texto).trim()) || (lista && lista.length > 0) || bool != null;
  }).length;

  return (
    <div className="px-2 sm:px-6 pb-12 sm:pb-16">
      <div
        className="max-w-[1280px] mx-auto rounded-2xl sm:rounded-3xl border p-3 sm:p-5 md:p-6 overflow-hidden space-y-5"
        style={{
          borderColor: 'rgba(15, 23, 42, 0.06)',
          background:
            'radial-gradient(900px circle at 10% -20%, rgba(251, 236, 214, 0.55) 0%, transparent 55%), radial-gradient(1000px circle at 90% 0%, rgba(214, 234, 248, 0.55) 0%, transparent 60%), #F6F4F1',
        }}
      >
      {/* Back link */}
      <button
        onClick={() => navigate('/feedback-hero/respostas')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 -ml-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
      >
        <ArrowLeft size={14} />
        Voltar para Respostas
      </button>

      {/* Session header */}
      <section className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_4px_16px_rgba(15,23,42,0.04)] p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-base font-bold border flex-shrink-0 ${
                naoIdentificado
                  ? 'bg-slate-100 text-slate-500 border-slate-200'
                  : isDone
                    ? 'bg-[#EAF7EE] text-[#1F5C3A] border-[#2E8B57]/25'
                    : 'bg-[#FFF4E4] text-[#7B4B0C] border-[#D7A64A]/30'
              }`}
            >
              {initials || <User size={20} />}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Sessão
              </p>
              <h2
                className={`text-xl sm:text-2xl font-bold tracking-tight ${
                  naoIdentificado ? 'text-slate-500 italic' : 'text-slate-800'
                }`}
              >
                {sessionName}
              </h2>
              {data.cliente_telefone && (
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                  <Phone size={12} />
                  {data.cliente_telefone}
                </p>
              )}
            </div>
          </div>

          {/* Status badge */}
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider border self-start ${
              isDone
                ? 'border-[#2E8B57]/30 text-[#1F5C3A] bg-[#EAF7EE]'
                : 'border-[#D7A64A]/40 text-[#7B4B0C] bg-[#FFF4E4]'
            }`}
          >
            {isDone ? <CheckCircle2 size={13} /> : <Clock3 size={13} />}
            {isDone ? 'Finalizada' : 'Em andamento'}
          </span>
        </div>

        {/* Info row */}
        <div className="mt-5 flex flex-wrap gap-2.5">
          <InfoBlock
            icon={CalendarClock}
            label="Início"
            value={formatDate(data.iniciado_em) || '—'}
            tone="bg-[#EAF7EE] text-[#1F5C3A]"
          />
          <InfoBlock
            icon={CalendarClock}
            label="Fim"
            value={formatDate(data.finalizado_em) || '—'}
            tone={isDone ? 'bg-[#EAF7EE] text-[#1F5C3A]' : 'bg-slate-100 text-slate-400'}
          />
          <InfoBlock
            icon={MessageSquareText}
            label="Respostas"
            value={`${respondidas} / ${totalRespostas}`}
            tone="bg-slate-100 text-slate-600"
          />
        </div>
      </section>

      {/* Respostas */}
      <section className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_4px_16px_rgba(15,23,42,0.04)] overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200/70 bg-gradient-to-br from-white to-[#EAF7EE]/20">
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <MessageSquareText size={16} className="text-[#1F5C3A]" />
            Respostas
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Cada bloco apresenta a pergunta, o tipo e a resposta do cliente.
          </p>
        </div>

        {respostas.length === 0 && (
          <div className="px-5 py-10 text-center">
            <HelpCircle size={28} className="text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-700">Nenhuma resposta registrada.</p>
            <p className="text-xs text-slate-500 mt-1">
              Esta sessão foi iniciada mas o cliente não respondeu nenhuma pergunta.
            </p>
          </div>
        )}

        <ul className="divide-y divide-slate-100">
          {respostas.map((r, i) => {
            const pergunta =
              r.pergunta_texto ||
              r.texto_pergunta ||
              r.pergunta?.texto ||
              r.pergunta?.titulo ||
              r.pergunta?.texto_pergunta ||
              `Pergunta #${r.pergunta_id ?? i + 1}`;

            const rawType =
              r.tipo ||
              r.pergunta?.tipo ||
              r.tipo_pergunta ||
              r.pergunta_tipo ||
              '';
            const tipoKey = String(rawType || '').toLowerCase().trim();
            const typeMeta = TYPE_META[tipoKey] || null;
            const TypeIconCmp = typeMeta?.icon || HelpCircle;

            const numero =
              r.resposta_numero ??
              r.respostaNumero ??
              (typeof r.resposta === 'number' ? r.resposta : null);
            const texto =
              r.resposta_texto ??
              r.respostaTexto ??
              (typeof r.resposta === 'string' ? r.resposta : '');
            const lista =
              r.resposta_lista ??
              r.respostaLista ??
              (Array.isArray(r.resposta) ? r.resposta : null);
            const boolRaw =
              r.resposta_bool ??
              r.respostaBool ??
              r.respostaBoolean ??
              null;

            const inferredKind =
              numero != null
                ? 'number'
                : lista && lista.length
                  ? 'list'
                  : boolRaw != null
                    ? 'boolean'
                    : 'text';
            const kind = typeMeta?.kind || inferredKind;

            const boolValue =
              boolRaw != null
                ? boolRaw
                  ? 'Sim'
                  : 'Não'
                : tipoKey === 'sim_nao' && texto
                  ? texto
                  : '';

            const responseAt =
              r.criado_em || r.respondido_em || r.created_at || r.updated_at || '';
            const hasResponse =
              numero != null ||
              (texto && String(texto).trim()) ||
              (lista && lista.length > 0) ||
              boolRaw != null ||
              (tipoKey === 'sim_nao' && Boolean(texto));

            return (
              <li
                key={i}
                className={`px-5 py-5 transition ${
                  hasResponse ? 'hover:bg-[#EAF7EE]/20' : 'bg-slate-50/40'
                }`}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                  {/* Pergunta */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[#EAF7EE] border border-[#2E8B57]/20 text-[10px] font-bold text-[#1F5C3A]">
                        {i + 1}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        Pergunta
                      </span>
                    </div>
                    <div className="text-[16px] font-semibold text-slate-800 leading-snug break-words">
                      {pergunta}
                    </div>
                    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide border ${
                          typeMeta?.badgeClass ||
                          'border-slate-200 text-slate-600 bg-slate-50'
                        }`}
                      >
                        <TypeIconCmp size={10} />
                        {typeMeta?.label || 'Resposta'}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide border border-slate-200 text-slate-500 bg-slate-50">
                        {KIND_LABEL[kind] || 'Texto'}
                      </span>
                    </div>
                  </div>

                  {/* Resposta */}
                  <div className="w-full lg:w-[340px] lg:flex-shrink-0">
                    <div
                      className={`rounded-xl border p-4 ${
                        hasResponse
                          ? 'border-slate-200 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.03)]'
                          : 'border-dashed border-slate-300 bg-slate-50/60'
                      }`}
                    >
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        Resposta do cliente
                      </div>

                      {!hasResponse && (
                        <div className="mt-2 text-sm text-slate-400 italic">
                          Sem resposta
                        </div>
                      )}

                      {hasResponse && kind === 'number' && (
                        <div className="mt-2.5 flex items-end gap-2">
                          <div className="text-4xl font-bold text-[#1F5C3A] leading-none">
                            {numero}
                          </div>
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 pb-1">
                            {typeMeta?.valueLabel || 'Valor'}
                          </div>
                        </div>
                      )}

                      {hasResponse && kind === 'boolean' && (
                        <div className="mt-2.5">
                          <span
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-bold uppercase tracking-wide border ${
                              boolValue === 'Sim'
                                ? 'border-[#2E8B57]/30 text-[#1F5C3A] bg-[#EAF7EE]'
                                : 'border-[#D46D63]/30 text-[#7A2F2A] bg-[#FCEAE7]'
                            }`}
                          >
                            {boolValue === 'Sim' ? (
                              <CheckCircle2 size={13} />
                            ) : (
                              <ToggleLeft size={13} />
                            )}
                            {boolValue || '—'}
                          </span>
                        </div>
                      )}

                      {hasResponse && kind === 'list' && (
                        <div className="mt-2.5 flex flex-wrap gap-1.5">
                          {(lista || []).map((item, idx) => (
                            <span
                              key={`${item}-${idx}`}
                              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border border-indigo-200 text-indigo-700 bg-indigo-50"
                            >
                              {String(item)}
                            </span>
                          ))}
                        </div>
                      )}

                      {hasResponse && kind === 'text' && (
                        <div className="mt-2 text-[15px] text-slate-700 leading-relaxed border-l-2 border-[#2E8B57]/40 pl-3 italic font-medium">
                          {String(texto || '').trim()}
                        </div>
                      )}
                    </div>
                    {responseAt && (
                      <div className="mt-1.5 text-[11px] text-slate-400 flex items-center gap-1">
                        <CalendarClock size={10} />
                        {formatDate(responseAt)}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
      </div>
    </div>
  );
};

export default SessionDetail;
